//@ts-check
// Main entry point - imports modules and exposes functions needed by Alpine/HTML
import Alpine from "alpinejs";
import * as config from "./constants.js";
import {
  generateContractors,
  openContractorModal,
  closeContractorModal,
  processTeamRegeneration,
  updateReservationTimers,
  checkExpiredReservations,
  displayContractors,
  createContractorCard,
  reserveContractorFromModal,
} from "./modules/contractor.js";
import { nextContractorPollDelay, CONTRACTOR_POLL_MIN_MS } from "./modules/contractor-core.js";
import { contractorManager, refreshAlpineContractors, createAlpineContractorStore } from "./modules/contractorManager.js";
import {
  displayCharacter,
  addCharacterCreationButtonEvents,
  updateCreateButton,
  createNewCharacter,
  updateEnergyOverTime,
  upgradeSkill,
  unlockSkillSynergy,
  displayCharacterSelector,
  equipArtifact,
  unequipArtifact,
  showTechniqueGearSelector,
  closeTechniqueGearSelector,
  equipTechniqueGear,
  unequipTechniqueGear,
  showConfirmModal,
} from "./modules/character.js";
import { initializeInventory, updateInventoryCharacterHeader, generateTestInventory, createInventoryCard, clearInventory, sellItem,
         purchaseConsumable, useConsumable, openPurchaseModal, closePurchaseModal, setupPurchaseModalHandler, incrementCraftQty, decrementCraftQty,
         toggleEnergyPopup, buyEnergyDirect,
         updateCraftCost, getMaxCraftableQty, incrementSellQty, decrementSellQty, updateSellQty } from "./modules/inventory.js";
import { inventoryManager, refreshAlpineInventory, createAlpineInventoryStore, inventoryPaneHtml } from "./modules/inventoryManager.js";
import { gameState, loadGameState, saveGameState } from "./modules/gameState.js";
import { withAllFactionSkills } from "./modules/gameState-core.js";
import { renderCommandTab, updateCommandHeader, autoScoutPath, autoReserveContractors } from "./modules/command.js";
import { renderArchiveTab } from "./modules/archive.js";
import { renderSyndicateTab, promoteAndSucceed } from "./modules/syndicate.js";
import { renderOperationsTab, setOperationsView, settleOperationsTick, settleOperationsOnLoad, tickWorldClock } from "./modules/operations.js";
import { pickRaidSite } from "./modules/worldClock-core.js";
import { autoRaidTick } from "./modules/autoRaid.js";
import { autoFabricatorTick } from "./modules/autoFabricator.js";
import { addNotification } from "./modules/notifications.js";
import { multiplayer, cleanupMultiplayerStorage, checkCharacterNameUnique, validateCharacterName } from "./modules/multiplayer.js";
import { debug, isDebug, CH } from './modules/debugLog.js';
import {
  updateGridTeamDisplay,
  updateGridDisplay,
  updatePathDisplay,
  updateRaidButtonStates,
  displayRaidGrid,
  updateGridHeader,
  applyCompletedRaidState,
  updateGridPathHighlight,
  highlightValidMoves,
  updateCurrentRaidInfo,
  updateStartRaidButton,
  matchPathRoomsHeightToGrid,
  generateRaidGrid,
  getPathRequiredSkills,
  getTeamSkillLevels,
  releaseReservedContractor,
  restoreLogEntries,
  updateRaidGridForProgress,
  loopRaidProgression,
  updateItemPicker,
  resetItemHighlight,
  setRaidPanelTab,
  startRaid,
  resetRaid,
} from "./modules/raid.js";
import { getRandomAvatar, debounce, showModal, convertMarkdownToHTML } from "./modules/util.js";
import { observeAvatars } from "./modules/avatarView.js";
import { GAME_VERSION } from "./version.js";
import { buildGameplayGuide } from "./modules/gameplay-guide.js";
import { initDevViewport } from "./modules/devViewport.js";
import { isLocal } from "./modules/devBar.js";
import { initDevFixtures, loadFixture, restoreSave, logDevFixtures } from "./modules/devFixtures.js";

// Import game.js which imports everything else as dependencies
import {
  updateUI,
  updateTabVisibility,
  canAccessRaid,
  updateRaidSkillsInfo,
  updateFactionAvailability,
  updateHeaderBackground,
  checkMarketplaceExpiryAndPayments,
  claimLegacyCharacters,
  syncMarketplaceListings,
  generateTierSlider,
  generateRegionTiles,
  refreshRegionClocks,
} from "./modules/game.js";
import {
  initLayerNav, renderLayerToolbar, refreshToolbarValues, noteScreenVisited, currentScreen,
} from "./modules/layerNav.js";
import { craftItem, unlockArtifact, showCraftingModal, showCraftingTreeModal, clearCraftingTreeFilter, calculateBulkCraftingCost, getBulkCraftingCostPercent } from "./modules/crafting.js";
import { setItemDropRateMultipliers, setCreditsScaling, setCraftingCostScaling, setEnergyPurchaseScaling } from "./modules/raidUtil.js";
import { assertConfigLoaded } from "./modules/configValidation.js";
import { applyCraftingDiscountForTour } from "./modules/command-core.js";
import { returnValue } from "happy-dom/lib/PropertySymbol.js";

// Expose only what Alpine templates need in global scope
window.createContractorCard = createContractorCard;
window.getPathRequiredSkills = getPathRequiredSkills;
window.getTeamSkillLevels = getTeamSkillLevels;
window.contractorManager = contractorManager;
window.inventoryManager = inventoryManager;
window.gameState = gameState;
window.config = config;

// Dev-only QA helpers (localhost): load a maintained save fixture to exercise
// features (e.g. the whole Command layer) without grinding a save, then put the
// real save back. The catalogue, the loader and the button strip all live in
// devFixtures.js; these globals are the console entry points the qa-player
// skill drives.
if (isLocal()) {
  window.loadFixture = loadFixture;
  window.restoreSave = restoreSave;
  logDevFixtures();
}
window.Alpine = Alpine;
window.craftItem = craftItem;
window.unlockArtifact = unlockArtifact;
window.showCraftingModal = showCraftingModal;
window.showCraftingTreeModal = showCraftingTreeModal;
window.clearCraftingTreeFilter = clearCraftingTreeFilter;
window.openContractorModal = openContractorModal;
window.reserveContractorFromModal = reserveContractorFromModal;
window.generateTestInventory = generateTestInventory;
window.createInventoryCard = createInventoryCard;
window.clearInventory = clearInventory;
window.upgradeSkill = upgradeSkill;
window.unlockSkillSynergy = unlockSkillSynergy;
window.releaseReservedContractor = releaseReservedContractor;
window.equipArtifact = equipArtifact;
window.unequipArtifact = unequipArtifact;
window.showTechniqueGearSelector = showTechniqueGearSelector;
window.equipTechniqueGear = equipTechniqueGear;
window.unequipTechniqueGear = unequipTechniqueGear;
window.closeTechniqueGearSelector = closeTechniqueGearSelector;
window.equipTechniqueGear = equipTechniqueGear;
window.unequipTechniqueGear = unequipTechniqueGear;
window.sellItem = sellItem;
window.purchaseConsumable = purchaseConsumable;
window.useConsumable = useConsumable;
window.openPurchaseModal = openPurchaseModal;
window.closePurchaseModal = closePurchaseModal;
window.toggleEnergyPopup = toggleEnergyPopup;
window.buyEnergyDirect = buyEnergyDirect;
window.refreshAlpineContractors = refreshAlpineContractors;
window.setupPurchaseModalHandler = setupPurchaseModalHandler;
window.calculateBulkCraftingCost = calculateBulkCraftingCost;
window.getBulkCraftingCostPercent = getBulkCraftingCostPercent;
window.incrementCraftQty = incrementCraftQty;
window.decrementCraftQty = decrementCraftQty;
window.updateCraftCost = updateCraftCost;
window.getMaxCraftableQty = getMaxCraftableQty;
window.incrementSellQty = incrementSellQty;
window.decrementSellQty = decrementSellQty;
window.updateSellQty = updateSellQty;

// Reinitialize Tippy tooltips for dynamically created inventory cards
window.reinitializeInventoryTooltips = function() {
  if (window.tippy) {
    // Idempotent: tear down any existing instances first so callers across
    // render paths (inventory, crafting modal, gear tab) never stack duplicates
    document.querySelectorAll(".inventory-tooltip").forEach((el) => el._tippy?.destroy());
    tippy(".inventory-tooltip", {
      theme: "dark",
      placement: "top",
      delay: [300, 0],
      maxWidth: 300,
      allowHTML: true,
      content(reference) {
        const descDiv = reference.querySelector(".inventory-tooltip-content");
        return descDiv ? descDiv.innerHTML : "";
      },
      onMount(instance) {
        instance.popper.style.border = "2px solid white";
        instance.popper.style.borderRadius = "0.5rem";
      },
      onShow(instance) {
        // Create a named function so it can be accurately removed later
        instance._scrollHandler = () => instance.hide();
        window.addEventListener('scroll', instance._scrollHandler, { passive: true });
      },
      onHide(instance) {
        // Clean up the listener to prevent memory leaks
        window.removeEventListener('scroll', instance._scrollHandler);
      }
    });
  }
};

// Current contractor-list poll delay (see pollContractorList). Module scope so
// the tab-switch handler can reset the backoff when the section is reopened.
let contractorPollDelay = CONTRACTOR_POLL_MIN_MS;

/**
 * Boot-blocking error banner. Config problems mean the generated constants are
 * stale/partial, so the page must not come up half-working — a console-only
 * error would look like a blank screen with no cause.
 */
function showBootFailure(err) {
  const banner = document.createElement("pre");
  banner.style.cssText =
    "position:fixed;inset:0;z-index:9999;margin:0;padding:2rem;overflow:auto;" +
    "background:#1a0d0d;color:#ffb4b4;font:13px/1.5 ui-monospace,monospace;white-space:pre-wrap;";
  banner.textContent = `Idle Extraction failed to start.\n\n${err.message}`;
  document.body.appendChild(banner);
}

// Initialize game
debug(CH.ui, "adding dom event listener");
document.addEventListener("DOMContentLoaded", () => {
  // Fail fast on a stale or partial js/constants.js BEFORE anything reads a
  // balance value. Balance reads are direct (no inline fallbacks), so a missing
  // config must stop boot loudly rather than silently run a different game.
  try {
    assertConfigLoaded(config);
  } catch (err) {
    console.error(err);
    showBootFailure(err);
    return;
  }

  // Hydrate procedural avatars as they enter the DOM (covers every render path,
  // including Alpine/raid.js reactive re-renders). Install before any UI renders.
  observeAvatars();

  // Try to load saved game first
  debug(CH.ui, "doc loaded:", document);

  const loaded = loadGameState();
  debug(CH.ui, "loaded data: ", loaded);
  if (!loaded) {
    return;
  }

  // Operations that arrived while away, capped at one round per slot so a week
  // off pays the same as a night off. Called from here rather than inside
  // loadGameState: gameState.js importing operations.js would close a cycle
  // (operations -> gameState -> operations), which breaks boot silently.
  settleOperationsOnLoad();

  // Initialize per-item drop rate multipliers from itemRarities config
  const itemDropRates = {};
  Object.values(config.itemRarities).forEach(rarity => {
    if (rarity.dropChance?.perItem) {
      Object.assign(itemDropRates, rarity.dropChance.perItem);
    }
  });
  if (Object.keys(itemDropRates).length > 0) {
    setItemDropRateMultipliers(itemDropRates);
  }

  // Apply credit and crafting cost scaling from progressionScaling (single source of truth,
  // shared with the headless simulator). Unguarded: assertConfigLoaded above
  // already proved these exist, so a guard here could only hide a real problem.
  setCreditsScaling(config.progressionScaling.creditsScaling);
  setCraftingCostScaling(config.progressionScaling.craftingCostScaling);
  setEnergyPurchaseScaling(config.progressionScaling.energyPurchaseScaling);

  // Re-apply the per-tour crafting discount AFTER the config seed above, which
  // would otherwise wipe it. This is the reload path that used to lose it.
  applyCraftingDiscountForTour();

  if (gameState.activeCharacterId) {
    // Load their inventory (already done above in inventory loading section)
    // Update UI components
    document.getElementById("character-creator").classList.add("hidden");
    document.getElementById("character-display").classList.remove("hidden");
    displayCharacter();

    // Update character selector to show correct active character
    if (typeof displayCharacterSelector === "function") {
      displayCharacterSelector();
    }

    // Update inventory header
    if (typeof updateInventoryCharacterHeader === "function") {
      updateInventoryCharacterHeader();
    }
  }

  // Resume raid if it was in progress (only if not completed)
  if (gameState.currentRaid.raidInProgress && !gameState.currentRaid.raidCompleted) {
    // Show team
    document.getElementById("raid-team").classList.remove("hidden");

    // Restore log entries
    restoreLogEntries();
    updateRaidGridForProgress();
    loopRaidProgression();
  }

  // No need to sync contractor.reserved flags - we use only reservedContractors list
  // Update team display immediately after contractor sync to ensure reserved contractors show
  if (document.getElementById("grid-team-display")) {
    updateGridTeamDisplay();
    updateGridDisplay();
  }

  // Build the layer toolbar before the first visibility pass, so the screen
  // tabs it operates on exist.
  initLayerNav();

  updateUI();
  updateTabVisibility();

  // Update tab visibility again after character restoration to account for raid ownership
  setTimeout(() => {
    updateTabVisibility();
    renderLayerToolbar();
  }, 100);

  // Clean up multiplayer storage after game state is loaded
  cleanupMultiplayerStorage();

  // Refresh Alpine stores after loading game state
  refreshAlpineInventory();

  refreshAlpineContractors();

  // Restore raid grid UI if it exists
  if (gameState.currentRaid.currentRaidGrid && document.getElementById("raid-grid-section")) {
    document.getElementById("raid-grid-section").classList.remove("hidden");
    document.getElementById("raid-team").classList.remove("hidden");
    document.getElementById("raid-setup").classList.add("hidden");
    document.getElementById("raid-active").classList.remove("hidden");

    // Update raid active info
    updateCurrentRaidInfo();
    
    debug(CH.ui, "Restoring raid grid display");
    displayRaidGrid();
    updateGridHeader(); // Restore grid header with area info and room count

    // Restore the planning-only item-drop highlight picker (it is populated in
    // the generate-grid handler, which a reload doesn't run). Only in the
    // planning phase — hidden once a raid is in progress or completed.
    if (!gameState.currentRaid.raidInProgress && !gameState.currentRaid.raidCompleted) {
      updateItemPicker();
    }

    // Check if we need to restore completed raid visualization
    if (gameState.currentRaid.raidCompleted && gameState.currentRaid.completedRaidInfo) {
      debug(CH.ui, "Restoring completed raid visualization in DOMContentLoaded");
      // Apply completed raid state directly after displayRaidGrid
      applyCompletedRaidState(gameState.currentRaid.completedRaidInfo);

      // Restore log entries
      if (gameState.currentRaid.completedRaidInfo.logEntries && gameState.currentRaid.completedRaidInfo.logEntries.length > 0) {
        const logEntries = document.getElementById("log-entries");
        if (logEntries) {
          logEntries.innerHTML = "";
          gameState.currentRaid.completedRaidInfo.logEntries.forEach((logEntry) => {
            const entry = document.createElement("div");
            entry.className = `log-entry log-${logEntry.type}`;
            entry.textContent = logEntry.message;
            logEntries.appendChild(entry);
          });
          logEntries.scrollTop = logEntries.scrollHeight;
        }
      }
    } else {
      // Only call these for non-completed raids
      updateGridPathHighlight();
      highlightValidMoves();
    }
    updatePathDisplay();
    updateCurrentRaidInfo();
    updateStartRaidButton();
  }

  // Monotonic id so a fade-out that is still running can tell it has been
  // superseded by a newer navigation. See activateOnly below.
  let tabTransitionId = 0;

  // Tab switching with animations.
  //
  // DELEGATED rather than bound per button: screen tabs and the active-task
  // button are re-rendered from the layer registry, so listeners attached at
  // boot would be discarded on the first layer switch.
  document.addEventListener("click", (event) => {
    const btn = event.target.closest?.(".tab-btn");
    if (!btn) return;
    {
      const tabName = btn.getAttribute("data-tab");
      if (tabName == null || tabName == "") return;

      const newTab = document.getElementById(`${tabName}-tab`);

      // One screen can host more than one VIEW. Operations is both a planner
      // and a transit board, and switching between them is a real navigation
      // even though the tab does not change — so this runs BEFORE the
      // already-here guard below, which would otherwise swallow it.
      if (tabName === "operations") {
        // `data-mode` is set only by the toolbar's "New Operation" button; the
        // screen tab carries none, so the default is the transit board.
        setOperationsView(btn.dataset.mode === "plan" ? "plan" : "transit");
      }

      // Don't switch if we are already on (or heading to) this screen.
      //
      // Compares tracked intent rather than the button's `active` class: the
      // class is painted from a re-render that can happen mid fade-out, so a
      // stale one used to make this swallow a legitimate navigation.
      if (tabName === currentScreen()) return;

      // Check raid access for raid tab
      if (tabName === "raid" && !canAccessRaid()) {
        const raidOwner = gameState.characters.find((c) => c.id === gameState.currentRaid.raidOwnerId);
        const ownerName = raidOwner ? raidOwner.name : "another character";
        addNotification(`Cannot access raid tab - ${ownerName} has an active raid`, "error");
        return;
      }

      if (tabName === "contractors") {
        // Refresh contractor list when opening contractors tab
        if (Alpine.store("contractors")) {
          Alpine.store("contractors").refresh();
        }
        // Reopening is fresh interest — restart the poll backoff so the next few
        // updates are prompt rather than inheriting a settled 5-minute cadence.
        contractorPollDelay = CONTRACTOR_POLL_MIN_MS;
      }

      if (tabName === "character") {
        // Inventory and Gear are now sub-tabs of the character page; refresh
        // the active sub-pane so its content is current on return
        window.applyCharSubTab?.();
        // Re-render the card so its state (e.g. edit buttons locked during a
        // raid) is current on every return, not just after an edit/level-up.
        if (gameState.character) displayCharacter();
        else displayCharacterSelector?.();

        // With no character this screen IS the creator, whichever route got
        // here. Asserting it on arrival rather than trusting whoever emptied the
        // roster to have set it: promotion and founding both call
        // showCharacterScreenAfterPromotion(), but the layer switcher and the
        // switcher panel are two more ways in, and a blank Field screen is the
        // failure they produced.
        if (!gameState.character) {
          document.getElementById("character-creator")?.classList.remove("hidden");
          document.getElementById("character-display")?.classList.add("hidden");
        }
      }

      if (tabName === "command") {
        // Re-render so marks, upgrade affordability, and gallery are current
        renderCommandTab();
      }

      if (tabName === "archive") {
        // Keys arrive while the player is on the raid screen, so the Archive is
        // never current on arrival.
        renderArchiveTab();
      }

      if (tabName === "command-vault" || tabName === "syndicate-stores") {
        // The vaults change on promotion, founding and every convoy launch —
        // none of which is a thing the player does from these screens, so the
        // store has to be re-read on arrival rather than trusted to be current.
        refreshAlpineInventory();
      }

      if (tabName === "syndicate") {
        // Same reason: Writs, charter affordability, and founding eligibility
        // all move while the player is elsewhere.
        renderSyndicateTab();
      }

      // Find currently active tab content
      const currentActiveTab = document.querySelector(".tab-content.active");

      // Every activation goes through here, and it leaves EXACTLY ONE screen
      // active. That is not defensive tidying — it is the fix for a real stuck
      // state: switching layer and then immediately pressing the layer's task
      // button (New Operation) queued two fade-outs on the same outgoing tab,
      // because querySelector(".tab-content.active") still returned it the
      // second time. Both animationend handlers then fired and each activated
      // ITS OWN target, leaving two screens stacked and the page scrolling
      // twice its height until something re-clicked the tab.
      const activateOnly = (tab) => {
        document.querySelectorAll(".tab-content.active").forEach((t) => {
          if (t !== tab) t.classList.remove("active", "animate__animated", "animate__fadeOut");
        });
        tab.classList.add("active", "animate__animated", "animate__fadeIn");
        tab.addEventListener("animationend", function handleFadeIn() {
          tab.classList.remove("animate__animated", "animate__fadeIn");
          tab.removeEventListener("animationend", handleFadeIn);
        });
      };

      // A superseded transition must not finish: without the token, the older
      // handler still activates the screen the player already navigated away
      // from.
      const transition = ++tabTransitionId;

      // Animate out current tab
      if (currentActiveTab && currentActiveTab !== newTab) {
        currentActiveTab.classList.add("animate__animated", "animate__fadeOut");
        currentActiveTab.addEventListener("animationend", function handleFadeOut() {
          currentActiveTab.removeEventListener("animationend", handleFadeOut);
          currentActiveTab.classList.remove("active", "animate__animated", "animate__fadeOut");
          if (transition !== tabTransitionId) return; // a newer click won
          activateOnly(newTab);
        });
      } else {
        // No current tab, just show new one with animation
        activateOnly(newTab);
      }

      // Active styling is no longer inferred by matching Tailwind border
      // classes (which encoded each layer's accent as a magic string and had to
      // be edited per layer). renderLayerToolbar() re-renders the screen tabs
      // and colours the active one from the registry's `accent`.

      // Special handling for specific tabs
      if (tabName === "raid") {
        // Re-render region tiles: availability is per-character (Command
        // region locks), so tiles rendered at boot go stale after character
        // creation/switch/promotion or a Regional Charter purchase
        if (!gameState.currentRaid.currentRaidGrid && !gameState.currentRaid.raidInProgress) {
          generateRegionTiles();
        }
        setTimeout(matchPathRoomsHeightToGrid, 250); // Delay for animation
        setTimeout(updateRaidSkillsInfo, 250);
        setRaidPanelTab(gameState.currentRaid.raidInProgress ? 'log' : 'highlights');

      } else if (tabName === "character") {
        setTimeout(() => {}, 150); // Delay for animation to complete
      }

      // Remember where each layer was left, so switching back returns there.
      noteScreenVisited(tabName);
      // Reflect the new active screen in the toolbar (accent + active state).
      renderLayerToolbar();
    }
  });

  // Update faction availability when page loads
  updateFactionAvailability();

  // Command layer: reveal state + marks chip on boot
  updateCommandHeader();

  // Setup character management events
  addCharacterCreationButtonEvents();

  // Update inventory character header
  updateInventoryCharacterHeader();

  // Faction selection
  document.querySelectorAll(".faction-card").forEach((card) => {
    card.addEventListener("click", () => {
      const faction = card.dataset.faction;

      // Check if faction is disabled
      if (card.classList.contains("faction-disabled")) {
        addNotification("You already have a character of this faction", "error");
        return;
      }

      // Update selection with Tailwind classes
      document.querySelectorAll(".faction-card").forEach((c) => {
        c.classList.remove("selected", "text-gray-200", "animate-pulse");
        c.classList.add("text-gray-400");
      });
      card.classList.add("selected", "text-gray-200", "animate-pulse");
      card.classList.remove("text-gray-400");

      gameState.selectedFaction = faction;

      // Update header background based on selected faction
      updateHeaderBackground(faction);

      debug(CH.ui, "Faction selected:", faction);
      saveGameState();
      updateCreateButton();
    });
  });

  // Character name validation
  const nameInput = document.getElementById("character-name-create");
  const validationMessage = document.getElementById("name-validation-message");

  const debouncedValidate = debounce(async () => {
    const name = nameInput.value.trim();

    if (name) {
      validationMessage.className = "text-sm mt-1 text-gray-400";
      validationMessage.textContent = "Checking name availability...";
    }

    const result = await validateCharacterName(name);
    validationMessage.className = `text-sm mt-1 ${result.className}`;
    validationMessage.textContent = result.message;
    updateCreateButton();
  }, 500);

  nameInput.addEventListener("input", debouncedValidate);

  // Attach all event listeners
  document.getElementById("create-character").addEventListener("click", async () => {
    const characterName = nameInput.value.trim();

    if (!gameState.selectedFaction || !characterName || characterName.length < 2) {
      addNotification("❌ Please enter a valid name and select a faction", "error");
      return;
    }

    // Double-check name uniqueness before creating
    try {
      const isUnique = await checkCharacterNameUnique(characterName);
      if (!isUnique) {
        addNotification("❌ Character name already exists", "error");
        return;
      }
    } catch (error) {
      console.warn("Could not verify name uniqueness:", error);
    }

    // All faction skills at level 0. createNewCharacter applies the same rule,
    // so this is really just the shape the registration payload is sent in.
    const characterFactionSkills = withAllFactionSkills(gameState.selectedFaction);

    // Create character with all faction skills at level 0
    const characterData = {
      id: "player",
      name: characterName,
      faction: gameState.selectedFaction,
      skills: characterFactionSkills,
      maxHp: 100,
      currentHp: 100,
      injuries: [],
      level: 1,
      xp: 0,
      avatar: getRandomAvatar(),
    };

    let newCharacter = {};
    try {
      // Register character in multiplayer system
      const registeredCharacter = await multiplayer.registerCharacter(characterData);
      debug(CH.ui, "Registered character:", registeredCharacter);

      // Create new character using multi-character system
      newCharacter = createNewCharacter({
        ...characterData,
        multiplayerId: registeredCharacter.id || registeredCharacter.character?.id || null,
        ownerToken: registeredCharacter.ownerToken || null,
      });
    } catch (error) {
      // Not gated: the user is told multiplayer degraded, so the cause should
      // always be visible in the console.
      console.warn("Failed to register character:", error);
      addNotification("⚠️ Character created locally. Multiplayer features may be limited.", "warning");

      // Create new character using multi-character system (fallback)
      newCharacter = createNewCharacter({
        ...characterData,
      });
    }

    // Update gameState.character.skills to match character skills
    gameState.character.skills = { ...characterFactionSkills };

    debug(CH.ui, "Character created:", newCharacter);
    addNotification(`✅ Character "${characterName}" created and registered!`, "success");

    generateContractors();

    // Hide creator, show character display
    document.getElementById("character-creator").classList.add("hidden");
    document.getElementById("character-display").classList.remove("hidden");

    // Show character info
    displayCharacter();
    updateTabVisibility();
    updateUI();
    saveGameState();
  });

  // Initialize default tier if not set
  if (!gameState.currentRaid.selectedTier) {
    gameState.currentRaid.selectedTier = 1;
  }

  // Generate UI components on load
  generateTierSlider();
  generateRegionTiles();

  // Generate grid.
  //
  // A named function, not just a click handler, because Auto-Raid Protocol needs
  // the same work done. Clicking the button from automation looked equivalent
  // and is not: `#generate-grid` is DISABLED until the player has driven the
  // raid-setup UI, and a click on a disabled button silently does nothing — so
  // automation would sit there reporting "running" while achieving nothing.
  // Automation drives state, never widgets.
  const setupRaidGrid = () => {
    if (!gameState.currentRaid.selectedRegion || !gameState.currentRaid.selectedTier || !gameState.character) return false;

    // Set raid ownership to current character when they start planning a raid
    gameState.currentRaid.raidOwnerId = gameState.activeCharacterId;

    // Update tab visibility now that raid is owned by current character
    updateTabVisibility();

    // Clear any existing grid data to ensure fresh generation
    gameState.currentRaid.currentRaidGrid = null;
    gameState.currentRaid.selectedPath = [];

    // Generate completely new grid with region and tier
    gameState.currentRaid.currentRaidGrid = generateRaidGrid(gameState.currentRaid.selectedRegion, gameState.currentRaid.selectedTier);

    // WHERE on the planet this raid is (M3 S9). Rolled once, here, and saved
    // with the raid: a region spans four to six hours of longitude, so two
    // raids in the same region land at different times of day — but re-rolling
    // on read would make the sky flicker between paints and shift across a
    // reload. Only the world clock reads it today; it is a location, so
    // anything later that cares where a raid is can read it too.
    gameState.currentRaid.raidSite = pickRaidSite(gameState.currentRaid.selectedRegion);

    debug(CH.ui, "Generated raid grid:", gameState.currentRaid.currentRaidGrid);

    // Auto-select start position
    const startPos = gameState.currentRaid.currentRaidGrid.startPos;
    const startRoom = gameState.currentRaid.currentRaidGrid.grid[startPos.row][startPos.col];
    gameState.currentRaid.selectedPath = [{ row: startPos.row, col: startPos.col, room: startRoom }];

    displayRaidGrid();
    updateGridHeader(); // Initialize grid header with area info and room count
    updateGridTeamDisplay();
    updateGridDisplay();
    updateGridPathHighlight();
    highlightValidMoves(); // Show initial valid moves

    // Populate the item-drop highlight picker for the freshly generated grid
    resetItemHighlight();
    updateItemPicker();
    setRaidPanelTab('highlights');

    // Show raid grid section and team section
    document.getElementById("raid-grid-section").classList.remove("hidden");
    document.getElementById("raid-team").classList.remove("hidden");

    // Hide region selection and show raid active state
    document.getElementById("raid-setup").classList.add("hidden");
    document.getElementById("raid-active").classList.remove("hidden");

    // Update current raid info
    updatePathDisplay();
    updateCurrentRaidInfo();
    updateUI();
    saveGameState();
    return true;
  };
  document.getElementById("generate-grid").addEventListener("click", setupRaidGrid);

  // Cancel raid button
  // Tear down the current raid. Split out of the click handler so the confirm
  // modal below drives it.
  const performRaidCancel = () => {
    // Release any server-side contractor reservations/hires this raid holds
    // before we drop them locally — cancelling abandons the raid without ever
    // reaching finishRaidCore, which is what normally releases them. Best-effort
    // (fire-and-forget); the server's stale-hire sweep is the backstop. Only
    // player contractors (multiplayerId) have server state; AI ones are skipped.
    const cancelActor = gameState.characters?.find((c) => c.id === gameState.activeCharacterId);
    if (cancelActor?.multiplayerId && typeof multiplayer !== "undefined") {
      for (const contractor of gameState.currentRaid.hiredContractors || []) {
        if (contractor?.multiplayerId) {
          multiplayer
            .releaseHiredContractor(contractor.multiplayerId, cancelActor.multiplayerId)
            .catch((err) => console.error("Failed to release hired contractor on cancel:", err));
        }
      }
      for (const contractor of gameState.currentRaid.reservedContractors || []) {
        if (contractor?.multiplayerId) {
          multiplayer
            .releaseContractor(contractor.multiplayerId, cancelActor.multiplayerId)
            .catch((err) => console.error("Failed to release reserved contractor on cancel:", err));
        }
      }
    }

    // Hide raid grid section
    document.getElementById("raid-grid-section").classList.add("hidden");
    document.getElementById("raid-team").classList.add("hidden");

    // Clear the planning-only item highlight + picker
    resetItemHighlight();

    // Show region selection and hide raid active state
    document.getElementById("raid-setup").classList.remove("hidden");
    document.getElementById("raid-active").classList.add("hidden");

    // Clear raid state - but keep selectedRegion/selectedTier so the setup
    // screen comes back pre-selected with the previous choices
    gameState.currentRaid.raidInProgress = null;
    gameState.currentRaid.raidCompleted = false;
    gameState.currentRaid.currentRaidGrid = null;
    gameState.currentRaid.selectedPath = [];
    gameState.currentRaid.raidOwnerId = null;
    gameState.currentRaid.completedRaidInfo = null;

    // Update tab visibility now that raid is cleared
    updateTabVisibility();

    // Release reserved contractors
    gameState.currentRaid.reservedContractors = [];
    gameState.currentRaid.hiredContractors = [];

    // The tile/segment .selected classes are untouched (setup was only
    // hidden); refresh the info panel + Generate button from the kept state
    updateRaidSkillsInfo();

    updateUI();
    updateRaidButtonStates();
    saveGameState();
  };

  document.getElementById("cancel-raid").addEventListener("click", () => {
    // Cancelling discards the run, so confirm first. Mid-raid the stakes are
    // real — energy was spent at raid start and isn't refunded, and progress and
    // gathered loot are lost — so that variant warns harder than clearing a
    // setup that hasn't cost anything yet.
    const raid = gameState.currentRaid.raidInProgress;
    const roomsCompleted = raid?.roomsCompleted || 0;
    const clearedNote = roomsCompleted > 0
      ? ` (${roomsCompleted} room${roomsCompleted === 1 ? "" : "s"} cleared)`
      : "";

    const bodyHtml = raid
      ? `<p class="mb-2">Abandon the raid in progress?</p>
         <p class="text-yellow-400">Your progress${clearedNote} and everything gathered so far are lost, and the energy already spent is not refunded.</p>`
      : `<p class="mb-2">Clear this raid setup?</p>
         <p class="text-xs text-gray-400">The generated map, chosen path and team are discarded. No energy has been spent yet, and your region and tier stay selected.</p>`;

    showConfirmModal({
      title: raid ? "⚠️ Abandon Raid?" : "🗺️ Clear Raid Setup?",
      bodyHtml,
      confirmText: raid ? "Abandon Raid" : "Clear Setup",
      cancelText: raid ? "Keep Raiding" : "Go Back",
      confirmClass: raid ? "bg-red-600 hover:bg-red-700" : "bg-yellow-600 hover:bg-yellow-700",
      onConfirm: performRaidCancel,
    });
  });

  // Clear path button
  /*
    document.getElementById('clear-path').addEventListener('click', () => {
        gameState.currentRaid.selectedPath = [];
        updatePathDisplay();
        updateGridPathHighlight();
            highlightValidMoves();
        saveGameState();
    });
    */

  // Close modal button
  document.getElementById("close-modal").addEventListener("click", () => {
    closeContractorModal();
  });

  // Modal background click to close (only if mousedown started on background)
  const contractorModal = document.getElementById("contractor-modal");
  let mousedownTarget = null;
  contractorModal.addEventListener("mousedown", (e) => {
    mousedownTarget = e.target;
  });

  contractorModal.addEventListener("click", (e) => {
    if (e.target.id === "contractor-modal" && mousedownTarget?.id === "contractor-modal") {
      closeContractorModal();
    }
  });

  // Setup purchase modal handlers
  setupPurchaseModalHandler();

  // Tab switching handled above with animations

  // Regenerate contractors test button
  document.getElementById("regenerate-contractors-btn").addEventListener("click", () => {
    if (gameState.character) {
      generateContractors();
      // Refresh Alpine.js contractors display
      refreshAlpineContractors();
      addNotification("🔄 Contractor list regenerated for testing", "info");
      saveGameState();
    }
  });

  debug(CH.ui, "Setting up periodic updates...");
  // Set up periodic energy regeneration and UI updates
  const regenIntervalMs = 5000;

  setInterval(() => {
    debug(CH.ui, "Processing team health regeneration (including self)...");
    const healthChanged = processTeamRegeneration(regenIntervalMs);

    // Update energy first
    updateEnergyOverTime();
    // Currency values and the active-task label come from the layer registry.
    // Values only - refreshToolbarValues() deliberately does not rebuild the
    // chips, so the energy popup survives being open across a tick.
    refreshToolbarValues();
    updateCommandHeader(); // one-way Command reveal (announce + re-render)

    // Update reservation timers every second
    if (typeof updateReservationTimers !== "undefined") {
      updateReservationTimers();
    }

    // Check for expired marketplace contractor reservations
    const contractorsExpired = checkExpiredReservations();

    // Update UI
    updateUI();
    if (healthChanged || contractorsExpired) {
      updateGridTeamDisplay();
      saveGameState();
    }

    // Auto-Raid Protocol advances at most one step per tick, reading only from
    // gameState — so a reload mid-raid resumes, and a player taking manual
    // control is noticed on the next tick rather than fought by a live promise.
    // The raid functions are injected rather than imported by autoRaid.js: they
    // live in DOM-heavy modules, and passing them keeps the cycle testable.
    // Operations arrive on their own clock; settle whatever is due.
    settleOperationsTick();

    // The world clock (M3 S9) turns whether or not anything else happens. Both
    // calls are no-ops off their own screens — the raid grid's own animation
    // loop drives the day clock above the map, so only these two surfaces need
    // the tick: region tiles in raid planning, and the world map's terminator.
    refreshRegionClocks();
    tickWorldClock();

    autoRaidTick({
      generateGrid: setupRaidGrid,
      autoScoutPath,
      autoReserveContractors,
      startRaid,
      resetRaid,
      promoteAndSucceed,
    }).catch((err) => console.error("Auto-Raid tick failed:", err));

    // Auto-Fabricator works the DEAD TIME — while a raid runs, or while the
    // raid cycle waits on energy. Crafting in the launch path would delay every
    // raid by a tick per component, and a tech-gear tree is several. The window
    // is decided here because only main.js sees both the raid and the cycle.
    // Auto-Fabricator: one craft per tick while it is switched on. It shares
    // nothing with the raid cycle above and cannot delay it, so it runs on the
    // same plain schedule rather than waiting for a window.
    autoFabricatorTick();
  }, regenIntervalMs); // Update every 5 seconds

  // Refresh the contractor list to show latest availability (both main list and
  // modal). A self-rescheduling timeout rather than setInterval, because the
  // cadence backs off: 10s, 20s, 40s ... settling at ~5m20s while the section
  // stays open, so a page left open all day stops hammering the API. Any poll
  // that finds the UI off-screen resets the cadence, so reopening the section
  // (which also refreshes immediately via the tab handler) is responsive again.
  const pollContractorList = () => {
    const contractorsTab = document.getElementById("contractors-tab");
    const modal = document.getElementById("contractor-modal");
    // document.hidden covers a backgrounded browser tab — no point polling for a
    // list nobody is looking at.
    const onScreen = (el) => !document.hidden && el && !el.classList.contains("hidden") && window.getComputedStyle(el).display !== "none";

    const contractorsTabVisible = onScreen(contractorsTab);
    const modalVisible = onScreen(modal);

    if (contractorsTabVisible || modalVisible) {
      // Refresh Alpine store (updates modal)
      if (Alpine.store("contractors")) {
        Alpine.store("contractors").refresh();
      }
      // Refresh main list display
      if (contractorsTabVisible) {
        displayContractors();
      }
      contractorPollDelay = nextContractorPollDelay(contractorPollDelay);
    } else {
      contractorPollDelay = CONTRACTOR_POLL_MIN_MS;
    }

    setTimeout(pollContractorList, contractorPollDelay);
  };
  setTimeout(pollContractorList, contractorPollDelay);

  // Auto-sync when coming back online
  window.addEventListener("online", () => {
    setTimeout(() => {
      multiplayer.syncLocalCharacters();
      debug(CH.ui, "Multiplayer system initialized");
    }, 1000); // Give a moment for connection to stabilize
  });

  // Startup marketplace reconciliation, ordered because each step feeds the next:
  //   1. re-onboard pre-token (legacy) characters so they have a multiplayerId,
  //   2. refresh their listing state from the server (local marketplaceData is a
  //      snapshot taken at listing time and can be stale),
  //   3. act on it — expire listings and collect any pending payouts.
  // Non-blocking; each step is best-effort and must not stop the next.
  claimLegacyCharacters()
    .catch((err) => console.error("Error claiming legacy characters:", err))
    .then(() => syncMarketplaceListings())
    .catch((err) => console.error("Error syncing marketplace listings:", err))
    .finally(() => checkMarketplaceExpiryAndPayments());

  setInterval(() => {
    checkMarketplaceExpiryAndPayments();
  }, 10 * 60 * 1000); // Update every 10 minutes

  // Initialize Tippy.js tooltips for all elements
  if (window.tippy) {
    // Initialize contractor tooltips
    tippy(".contractor-tooltip", {
      theme: "dark",
      placement: "top",
      delay: [500, 200],
      maxWidth: 400,
      allowHTML: true,
    });

    // Initialize skill tooltips
    tippy(".skill-tooltip", {
      theme: "dark",
      placement: "top",
      delay: [500, 200],
      maxWidth: 300,
      allowHTML: true,
    });

    // Initialize room tooltips (if they exist)
    tippy(".room-tooltip", {
      theme: "dark",
      placement: "top",
      delay: [500, 200],
      maxWidth: 350,
      allowHTML: true,
      content(reference) {
        return reference.dataset.tippyContent;
      },
    });

    // Initialize inventory item tooltips
    tippy(".inventory-tooltip", {
      theme: "dark",
      placement: "top",
      delay: [300, 0],
      maxWidth: 300,
      allowHTML: true,
      content(reference) {
        // Get the description from the hidden div if it exists
        const descDiv = reference.querySelector(".inventory-tooltip-content");
        return descDiv ? descDiv.innerHTML : "";
      },
      onMount(instance) {
        instance.popper.style.border = "2px solid white";
        instance.popper.style.borderRadius = "0.5rem";
      },
      onShow(instance) {
        // Create a named function so it can be accurately removed later
        instance._scrollHandler = () => instance.hide();
        window.addEventListener('scroll', instance._scrollHandler, { passive: true });
      },
      onHide(instance) {
        // Clean up the listener to prevent memory leaks
        window.removeEventListener('scroll', instance._scrollHandler);
      }
    });
  }

  // Show game UI with fade-in after everything is loaded
  const gameContainer = document.getElementById("game-container");
  if (gameContainer) {
    gameContainer.style.display = "";
    gameContainer.style.opacity = "0";
    gameContainer.style.transition = "opacity 0.3s ease-in";
    setTimeout(() => {
      gameContainer.style.opacity = "1";
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 10);
  }

  // How To Play button
  document.getElementById("how-to-btn").addEventListener("click", () => {
    // Built on open, not at boot: the Command reveal fires on a background tick,
    // so a guide captured earlier would still be hiding a layer the header shows.
    const html = convertMarkdownToHTML(buildGameplayGuide());

    showModal(
      "Gameplay Guide",
      `
            <div class="gameplay-guide prose prose-invert max-w-none max-h-[70vh] overflow-y-auto">
                ${html}
            </div>
        `
    );
  });

  document.getElementById("version-display").textContent = `v${GAME_VERSION}`;

  // Dev-only strip: tablet check, then one button per save fixture. Both
  // self-gate to localhost via devBar.js. Viewport first so it stays leftmost.
  initDevViewport();
  initDevFixtures();
});

document.addEventListener("alpine:init", () => {
  debug(CH.ui, "Initializing Alpine Stores");
  createAlpineContractorStore();
  createAlpineInventoryStore();

  // Vault screen bodies. Injected HERE, not later: alpine:init fires before
  // Alpine walks the DOM, so this markup gets initialized with everything else.
  // Injecting after Alpine.start() would need a manual initTree and would leave
  // the directives inert if that were ever forgotten.
  const paneBody = (id, source, accent, empty) => {
    const host = document.getElementById(id);
    if (host) host.innerHTML = inventoryPaneHtml(source, accent, empty);
  };
  paneBody("command-vault-body", "commandVault", "#b18cff",
    "Nothing outbound. Promote a character to send their kit onward.");
  paneBody("syndicate-stores-body", "syndicateStores", "#e0a84c",
    "Nothing on the dock. Founding delivers what the Guild Vault has consigned.");
});

Alpine.start();
